/**
 * Glossy, brochure-style print document for a complete employee profile.
 *
 * Rendered as a standalone HTML document inside a hidden iframe and handed to the browser's own
 * print dialog (which also provides "Save as PDF"). Building the document from scratch — rather than
 * cloning the live profile — keeps the layout free of the app's Tailwind cascade and dark-mode
 * overrides, so the brochure looks identical however the profile happens to be themed on screen.
 */

import { buildEmployeeBrochureDataProtectionNotice } from "./dataProtectionPrintNotice";

export type BrochureRow = Record<string, unknown>;

export type BrochureInput = {
  employee: BrochureRow;
  documents?: BrochureRow[];
  employeeCycles?: BrochureRow[];
  cycleEvents?: BrochureRow[];
  safetyBriefs?: BrochureRow[];
  /** Rendered in a dark, high-contrast palette when true. */
  dark?: boolean;
  /**
   * Ink-saving finish: drops the artwork, gradients and colour fills for plain black on white.
   * Takes precedence over `dark`, since a dark page defeats the purpose.
   */
  printFriendly?: boolean;
  /** Email/name of the person producing the brochure, stamped on the cover for traceability. */
  producedBy?: string;
  /**
   * The producer's stored signature as a data URI, pre-applied to the declaration. Omitted when they
   * have no signature on their profile, in which case the block stays a blank line to sign by hand.
   */
  producedBySignature?: string;
  /** Data-URI (or same-origin URL) for the GBR cover artwork. */
  backgroundImage?: string;
};

const BG_ASSET_URL = "/GBR_BG.png";

/* ------------------------------------------------------------------ helpers */

function esc(value: unknown): string {
  const s = value === null || value === undefined ? "" : String(value);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function text(value: unknown): string {
  const s = value === null || value === undefined ? "" : String(value).trim();
  return s;
}

function orDash(value: unknown): string {
  return text(value) || "—";
}

function fmtDate(value: unknown): string {
  const raw = text(value);
  if (!raw) return "—";
  const d = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(value: unknown): string {
  const raw = text(value);
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function yesNo(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  const truthy = value === true || value === 1 || ["1", "true", "yes"].includes(String(value).toLowerCase());
  return truthy ? "Yes" : "No";
}

function asArray<T = BrochureRow>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === "object") return Object.values(value as Record<string, T>);
  return [];
}

function parseJsonArray(value: unknown): BrochureRow[] {
  if (Array.isArray(value)) return value as BrochureRow[];
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as BrochureRow[]) : [];
  } catch {
    return [];
  }
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

/**
 * Draw a URL onto a canvas. Used when fetch() cannot read the bytes (CORS on a blob store) but the
 * browser can still paint the image, which is enough for the print document.
 */
function paintUrlToDataUrl(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, img.naturalWidth || img.width);
        canvas.height = Math.max(1, img.naturalHeight || img.height);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Fetch an image and inline it, so the iframe never races the network before `print()` fires. */
export async function loadImageAsDataUrl(url: string): Promise<string | null> {
  const raw = String(url ?? "").trim();
  if (!raw) return null;
  if (/^data:/i.test(raw)) return raw;
  try {
    const res = await fetch(raw, { cache: "no-store" });
    if (res.ok) {
      const blob = await res.blob();
      const data = await blobToDataUrl(blob);
      if (data) return data;
    }
  } catch {
    /* fall through to the paint path */
  }
  return paintUrlToDataUrl(raw);
}

/** Any inlinable image: data URIs (any MIME), blob URLs, or remote URLs converted to data URIs. */
async function resolvePrintableImage(url: unknown): Promise<string> {
  const raw = String(url ?? "").trim();
  if (!raw) return "";
  if (/^data:/i.test(raw)) return raw;
  return (await loadImageAsDataUrl(raw)) ?? "";
}

/* ------------------------------------------------------------------- palette */

type Palette = {
  pageBg: string;
  cardBg: string;
  cardBgAlt: string;
  ink: string;
  inkMuted: string;
  border: string;
  accent: string;
  accentSoft: string;
  headerFrom: string;
  headerTo: string;
  gloss: string;
  chipBg: string;
  tableHead: string;
  zebra: string;
  /**
   * Veil painted over the page-canvas artwork. The artwork is at full strength here (a canvas
   * background layer cannot be given its own opacity), so these alphas do all the lightening.
   */
  canvasVeil: string;
};

/*
 * Both colour palettes are light-on-dark. The artwork is painted on the page canvas so that it
 * always covers the full page, and one canvas serves every page — so the cover and the interior
 * pages must share a single treatment or the cover would end mid-page against a mismatched
 * background. Paper output is served by the mono variant instead of a light-on-white palette.
 */
function palette(dark: boolean): Palette {
  return dark
    ? {
        // Deeper, near-black: intended for on-screen reading.
        pageBg: "#080d1a",
        cardBg: "rgba(18,28,52,0.82)",
        cardBgAlt: "rgba(11,19,38,0.82)",
        ink: "#e8eefb",
        inkMuted: "#a3b4d0",
        border: "rgba(148,175,222,0.26)",
        accent: "#8fb2ff",
        accentSoft: "rgba(143,178,255,0.16)",
        headerFrom: "#050a16",
        headerTo: "#101c38",
        gloss: "rgba(255,255,255,0.07)",
        chipBg: "rgba(143,178,255,0.16)",
        tableHead: "rgba(143,178,255,0.12)",
        zebra: "rgba(255,255,255,0.03)",
        canvasVeil:
          "linear-gradient(160deg, rgba(5,10,22,0.90) 0%, rgba(5,10,22,0.94) 55%, rgba(5,10,22,0.91) 100%)",
      }
    : {
        // Branded navy with the artwork more visible than the dark variant.
        pageBg: "#0c1428",
        cardBg: "rgba(20,32,60,0.74)",
        cardBgAlt: "rgba(13,23,46,0.74)",
        ink: "#eef3ff",
        inkMuted: "#adc0dc",
        border: "rgba(166,195,244,0.30)",
        accent: "#9dc0ff",
        accentSoft: "rgba(157,192,255,0.16)",
        headerFrom: "#0b1f45",
        headerTo: "#1d4ed8",
        gloss: "rgba(255,255,255,0.10)",
        chipBg: "rgba(157,192,255,0.16)",
        tableHead: "rgba(157,192,255,0.14)",
        zebra: "rgba(255,255,255,0.035)",
        canvasVeil:
          "linear-gradient(160deg, rgba(8,16,36,0.74) 0%, rgba(8,16,36,0.84) 55%, rgba(8,16,36,0.78) 100%)",
      };
}

/* -------------------------------------------------------------------- styles */

function stylesheet(p: Palette, dark: boolean, bgImage: string, printFriendly: boolean): string {
  const pageFill = printFriendly ? "#ffffff" : p.pageBg;
  return `
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
  html, body {
    margin: 0; padding: 0;
    background-color: ${pageFill};
    color: ${p.ink};
  }
  body {
    font-family: "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
    font-size: 10pt;
    line-height: 1.45;
    -webkit-font-smoothing: antialiased;
  }
  @page { size: A4 portrait; margin: 0; background: ${pageFill}; }

  .sheet {
    position: relative;
    width: 210mm;
    /*
     * One sheet = one printed page. min-height fills short pages; max-height + overflow clip a 1px
     * spill so WebKit/iOS cannot emit a trailing blank page (and a white hairline) after every sheet.
     */
    min-height: 277mm;
    max-height: none;
    overflow: visible;
    padding: 12mm 14mm 14mm;
    margin: 0;
    background-color: ${pageFill};
    background-image: ${printFriendly ? "none" : `${p.canvasVeil}, url('${bgImage}')`};
    background-size: 210mm 297mm, 210mm 297mm;
    background-position: top left, top left;
    background-repeat: no-repeat, no-repeat;
    break-after: page;
    page-break-after: always;
  }
  .sheet:last-child { break-after: auto; page-break-after: auto; }

  /* ---------- cover ---------- */
  .cover {
    padding: 12mm 14mm 12mm;
    color: #f3f7ff;
  }
  .cover-art, .cover-tint, .cover-gloss, .sheet-gloss { display: none; }
  .cover-inner {
    position: relative;
    min-height: 253mm;
    display: flex;
    flex-direction: column;
  }
  .cover-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10mm; }
  /* Pushes name → notice down so the notice sits immediately above the footer. */
  .cover-main { margin-top: auto; }
  .cover-logo {
    max-height: 36mm; max-width: 95mm; object-fit: contain; object-position: left top;
    background: transparent;
    padding: 0;
  }
  .cover-brandmark { font-size: 8pt; letter-spacing: 0.24em; text-transform: uppercase; opacity: 0.85; text-align: right; }
  .cover-eyebrow {
    font-size: 8pt; letter-spacing: 0.34em; text-transform: uppercase;
    color: rgba(233,241,255,0.82);
  }
  .cover-title { font-size: 28pt; line-height: 1.05; font-weight: 800; margin: 2mm 0 0; letter-spacing: -0.02em; }
  .cover-sub { font-size: 12pt; font-weight: 500; color: rgba(233,241,255,0.9); margin: 2mm 0 0; }
  .cover-rule { height: 3px; width: 34mm; margin: 4mm 0; border-radius: 3px; background: linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0.15) 100%); }
  .cover-portrait {
    width: 28mm; height: 28mm; border-radius: 3.5mm; object-fit: cover;
    border: 2px solid rgba(255,255,255,0.55);
    box-shadow: 0 8px 26px rgba(3,10,26,0.45);
    background: rgba(255,255,255,0.08);
  }
  .cover-facts { display: grid; grid-template-columns: repeat(2, 1fr); gap: 3mm 7mm; margin-top: 3mm; }
  .cover-fact-k { font-size: 7pt; letter-spacing: 0.16em; text-transform: uppercase; color: rgba(226,236,255,0.68); }
  .cover-fact-v { font-size: 10.5pt; font-weight: 600; }
  .cover-foot {
    margin-top: 4mm; padding-top: 4mm;
    border-top: 1px solid rgba(255,255,255,0.22);
    display: flex; justify-content: space-between; gap: 8mm;
    font-size: 8pt; color: rgba(226,236,255,0.78);
  }
  .cover-badges { display: flex; flex-wrap: wrap; gap: 3mm; margin-top: 5mm; }
  .cover-badge {
    display: inline-block; padding: 2mm 4.5mm;
    border-radius: 999px; font-size: 7.5pt; font-weight: 700;
    letter-spacing: 0.16em; text-transform: uppercase;
    background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.34);
  }
  .cover-badge--locked { background: rgba(245,158,11,0.26); border-color: rgba(253,224,171,0.6); }
  .cover-notice { margin-top: 5mm; }
  .cover-notice .notice { margin-bottom: 0; padding: 3.2mm 4mm; }
  .cover-notice .notice-body { font-size: 8pt; }

  /* ---------- interior chrome ---------- */
  .page-head {
    display: flex; align-items: baseline; justify-content: space-between;
    padding-bottom: 3mm; margin-bottom: 6mm;
    border-bottom: 2px solid ${p.accent};
  }
  .page-head-title { font-size: 15pt; font-weight: 750; letter-spacing: -0.01em; }
  .page-head-meta { font-size: 7.5pt; letter-spacing: 0.14em; text-transform: uppercase; color: ${p.inkMuted}; }
  /* Gloss overlays are disabled: they overflow the sheet on iOS print and paint as white streaks. */
  .sheet-body { position: relative; padding-bottom: 10mm; }
  .page-foot {
    position: absolute; left: 14mm; right: 14mm; bottom: 7mm;
    display: flex; justify-content: space-between;
    font-size: 7pt; color: ${p.inkMuted};
    border-top: 1px solid ${p.border}; padding-top: 2.5mm;
  }

  /* ---------- cards ---------- */
  .card {
    position: relative;
    border: 1px solid ${p.border};
    border-radius: 3mm;
    background: linear-gradient(168deg, ${p.cardBg} 0%, ${p.cardBgAlt} 100%);
    padding: 5mm 5.5mm;
    margin-bottom: 5mm;
    overflow: hidden;
    break-inside: avoid; page-break-inside: avoid;
    box-shadow: 0 1px 2px rgba(3,8,20,0.35);
  }
  /* Card gloss highlight along the top edge. */
  .card::after {
    content: ""; position: absolute; top: 0; left: 0; right: 0; height: 40%;
    background: linear-gradient(180deg, ${p.gloss} 0%, rgba(255,255,255,0) 100%);
    pointer-events: none;
  }
  .card > * { position: relative; z-index: 1; }
  .card-title { font-size: 11pt; font-weight: 700; margin: 0 0 1mm; display: flex; align-items: center; gap: 3mm; }
  .card-title::before { content: ""; width: 3px; height: 4.4mm; border-radius: 2px; background: ${p.accent}; }
  .card-hint { font-size: 8pt; color: ${p.inkMuted}; margin: 0 0 3.5mm; }
  .card-empty { font-size: 9pt; color: ${p.inkMuted}; font-style: italic; margin: 0; }

  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3.5mm 6mm; }
  .grid-2 { grid-template-columns: repeat(2, 1fr); }
  .field-k { font-size: 7pt; letter-spacing: 0.14em; text-transform: uppercase; color: ${p.inkMuted}; }
  .field-v { font-size: 9.5pt; font-weight: 600; word-break: break-word; }

  table { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
  thead { display: table-header-group; }
  th {
    text-align: left; font-size: 6.8pt; letter-spacing: 0.14em; text-transform: uppercase;
    color: ${p.inkMuted}; background: ${p.tableHead};
    padding: 2.4mm 2.6mm; border-bottom: 1px solid ${p.border}; font-weight: 700;
  }
  td { padding: 2.4mm 2.6mm; border-bottom: 1px solid ${p.border}; vertical-align: top; }
  tbody tr:nth-child(even) td { background: ${p.zebra}; }
  tbody tr { break-inside: avoid; page-break-inside: avoid; }
  th:first-child, td:first-child { padding-left: 0.5mm; }

  .chip {
    display: inline-block; padding: 0.9mm 2.6mm; border-radius: 999px;
    font-size: 7.4pt; font-weight: 700; letter-spacing: 0.05em;
    background: ${p.chipBg}; color: ${p.accent}; border: 1px solid ${p.border};
    white-space: nowrap;
  }
  /* Both colour palettes are light-on-dark, so these need only the on-dark treatment. */
  .chip--ok { background: rgba(16,185,129,0.18); color: #6ee7b7; }
  .chip--warn { background: rgba(245,158,11,0.20); color: #fcd34d; }
  .chip--bad { background: rgba(244,63,94,0.18); color: #fda4af; }

  .notice {
    border: 1px solid rgba(251,191,36,0.45);
    background: rgba(120,73,6,0.34);
    border-radius: 3mm; padding: 4mm 5mm; margin-bottom: 4mm;
  }
  .notice-title { font-size: 10.5pt; font-weight: 750; margin: 0 0 1.6mm; color: #fcd34d; }
  .notice-body { font-size: 8.5pt; margin: 0; color: #fde9c4; }

  .lock-banner {
    border: 1px solid rgba(251,191,36,0.5);
    background: rgba(120,73,6,0.36);
    border-radius: 3mm; padding: 4mm 5mm; margin-bottom: 5mm;
    font-size: 8.8pt; color: #fde9c4;
  }

  .toc { list-style: none; margin: 0; padding: 0; }
  .toc li {
    display: flex; align-items: baseline; gap: 3mm;
    padding: 1.1mm 0; border-bottom: 1px dotted ${p.border};
    font-size: 9pt;
  }
  .toc-no { font-size: 8pt; font-weight: 700; color: ${p.accent}; min-width: 8mm; }
  .toc-count { margin-left: auto; padding-left: 4mm; font-size: 8pt; color: ${p.inkMuted}; white-space: nowrap; }
  .toc-chapter { display: block; font-weight: 650; }
  .toc-sections { display: block; font-size: 7.6pt; color: ${p.inkMuted}; }

  .section-label {
    font-size: 8pt; letter-spacing: 0.18em; text-transform: uppercase;
    color: ${p.accent}; margin: 0 0 2.5mm; font-weight: 750;
  }
  .section-label + .card { margin-top: 0; }
  .card + .section-label { margin-top: 7mm; }

  .stat-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4mm; margin-bottom: 4mm; }
  .stat {
    position: relative; overflow: hidden;
    border: 1px solid ${p.border}; border-radius: 3mm;
    background: linear-gradient(168deg, ${p.cardBg} 0%, ${p.cardBgAlt} 100%);
    padding: 3.2mm; text-align: center;
  }
  .stat::after {
    content: ""; position: absolute; top: 0; left: 0; right: 0; height: 55%;
    background: linear-gradient(180deg, ${p.gloss} 0%, rgba(255,255,255,0) 100%);
  }
  .stat-v { position: relative; font-size: 18pt; font-weight: 800; letter-spacing: -0.03em; color: ${p.accent}; }
  .stat-k { position: relative; font-size: 7pt; letter-spacing: 0.14em; text-transform: uppercase; color: ${p.inkMuted}; }

  .signoff { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10mm; margin-top: 6mm; }
  .signoff-line { border-bottom: 1px solid ${p.ink}; height: 22mm; }
  .signoff-line--signed { display: flex; align-items: flex-end; justify-content: flex-start; }
  .signoff-sig {
    max-height: 18mm; max-width: 100%; object-fit: contain; object-position: left bottom;
    background: #ffffff; border-radius: 1mm; padding: 1mm 2mm;
  }
  .signoff-k { font-size: 8pt; letter-spacing: 0.14em; text-transform: uppercase; color: ${p.inkMuted}; margin-top: 2mm; }
  .signoff-name { font-size: 10pt; font-weight: 600; margin-top: 1mm; }
  .declaration-block {
    margin-top: 8mm; padding-top: 7mm;
    border-top: 1px solid ${p.border};
    break-inside: avoid; page-break-inside: avoid; break-before: avoid; page-break-before: avoid;
  }
  .declaration-block .card-title { font-size: 13pt; margin-bottom: 2mm; }
  .declaration-block .card-hint { font-size: 9.5pt; line-height: 1.5; }
  ${printFriendly ? printFriendlyOverrides() : ""}
  `;
}

/**
 * Ink-saving variant. Appended last so it wins on equal specificity, which keeps the brochure's
 * structure and page breaks identical to the full-colour version — only the finish changes.
 * Status is carried by the label text (Pass / Fail / Expired), never by colour alone, so nothing is
 * lost in greyscale or for a reader with a colour vision deficiency.
 */
function printFriendlyOverrides(): string {
  return `
  /* ---------- print-friendly (mono) ---------- */
  html { background-image: none; background-color: #ffffff; color: #000000; }
  body { background: #ffffff; color: #000000; }
  .sheet { background-image: none; background-color: #ffffff; }
  .sheet-gloss, .cover-art, .cover-tint, .cover-gloss { display: none; }
  .card::after, .stat::after { display: none; }

  .cover { background: #ffffff; color: #000000; }
  .cover-inner { min-height: 253mm; }
  .cover-notice .notice {
    background: #ffffff; border-color: #000000;
  }
  .cover-notice .notice-title, .cover-notice .notice-body { color: #000000; }
  .cover-logo { background: transparent; box-shadow: none; padding: 0; }
  .cover-brandmark, .cover-eyebrow { color: #333333; opacity: 1; }
  .cover-sub { color: #222222; }
  .cover-rule { background: #000000; }
  .cover-portrait { border-color: #999999; box-shadow: none; background: #ffffff; }
  .cover-fact-k { color: #444444; }
  .cover-foot { border-top-color: #999999; color: #333333; }
  .cover-badge {
    background: #ffffff; border-color: #000000; color: #000000; backdrop-filter: none;
  }
  .cover-badge--locked { background: #ffffff; border-color: #000000; }

  .page-head { border-bottom-color: #000000; }
  .page-head-meta, .page-foot, .card-hint, .card-empty, .field-k,
  .signoff-k, .stat-k, .toc-count, .toc-sections { color: #444444; }
  .page-foot { border-top-color: #bbbbbb; }

  .card, .stat {
    background: #ffffff; border-color: #bbbbbb; box-shadow: none;
  }
  .card-title::before, .stat-v, .section-label, .toc-no { color: #000000; background: #000000; }
  .stat-v, .section-label, .toc-no { background: transparent; }

  th { background: #f2f2f2; color: #000000; border-bottom-color: #000000; }
  td { border-bottom-color: #cccccc; }
  tbody tr:nth-child(even) td { background: #ffffff; }

  /* Outlined chips: the wording carries the meaning, so no fills are needed. */
  .chip, .chip--ok, .chip--warn, .chip--bad {
    background: #ffffff; color: #000000; border-color: #666666;
  }
  .chip--bad { border-color: #000000; border-width: 1.5px; font-weight: 800; }

  .notice, .lock-banner { background: #ffffff; border-color: #000000; }
  .notice-title, .notice-body, .lock-banner { color: #000000; }

  .toc li { border-bottom-color: #cccccc; }
  .signoff-line { border-bottom-color: #000000; }

  /* Photos, logos and signatures are the largest remaining consumers of colour ink. */
  .cover-logo, .cover-portrait, .signoff-sig { filter: grayscale(1); }
  .signoff-sig { background: transparent; padding: 0; }
  `;
}

/* --------------------------------------------------------------- components */

function field(k: string, v: string): string {
  return `<div><div class="field-k">${esc(k)}</div><div class="field-v">${esc(v)}</div></div>`;
}

function card(title: string, body: string, hint?: string): string {
  return `<section class="card"><h3 class="card-title">${esc(title)}</h3>${
    hint ? `<p class="card-hint">${esc(hint)}</p>` : ""
  }${body}</section>`;
}

function empty(label: string): string {
  return `<p class="card-empty">${esc(label)}</p>`;
}

function table(headers: string[], rows: string[][]): string {
  if (!rows.length) return "";
  return `<table><thead><tr>${headers.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead><tbody>${rows
    .map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`)
    .join("")}</tbody></table>`;
}

/** Cells may already contain markup (chips), so they are escaped at the call site. */
function chipFor(value: unknown): string {
  let raw = text(value);
  if (!raw) return "—";
  const l = raw.toLowerCase();
  // Status enums are stored lowercase ("upcoming", "completed"); sentence-case them for print.
  if (raw === l && !raw.includes(" ")) raw = raw.charAt(0).toUpperCase() + raw.slice(1);
  let tone = "";
  if (/(pass|valid|active|complete|satisfactor|fit|approved|closed|current)/.test(l)) tone = " chip--ok";
  else if (/(due|pending|monitor|review|restrict|open|expiring|suspend|in progress)/.test(l)) tone = " chip--warn";
  else if (/(fail|expired|unfit|overdue|withdraw|revoked|breach|not fit)/.test(l)) tone = " chip--bad";
  return `<span class="chip${tone}">${esc(raw)}</span>`;
}

function expiryChip(value: unknown): string {
  const raw = text(value);
  if (!raw) return "—";
  const d = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(d.getTime())) return esc(raw);
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  const tone = days < 0 ? " chip--bad" : days <= 90 ? " chip--warn" : " chip--ok";
  return `<span class="chip${tone}">${esc(fmtDate(raw))}</span>`;
}

/* ------------------------------------------------------------------ sections */

/**
 * A section is one topic; a chapter is one printed spread. Several thin sections share a chapter so
 * the brochure does not waste a sheet on a two-row table.
 */
type Section = { title: string; count: number | null; html: string; chapter: string };
type Chapter = { title: string; sections: Section[] };

function groupIntoChapters(sections: Section[]): Chapter[] {
  const chapters: Chapter[] = [];
  for (const section of sections) {
    const last = chapters[chapters.length - 1];
    if (last && last.title === section.chapter) last.sections.push(section);
    else chapters.push({ title: section.chapter, sections: [section] });
  }
  return chapters;
}

function buildSections(input: BrochureInput): Section[] {
  const e = input.employee;
  const sections: Section[] = [];

  /* Personal & contact */
  sections.push({
    title: "Personal & contact details",
    chapter: "Personal details & employment",
    count: null,
    html:
      card(
        "Identity",
        `<div class="grid">
          ${field("Full name", orDash(e.full_name))}
          ${field("Employee ID", orDash(e.id))}
          ${field("Date of birth", fmtDate(e.dob))}
          ${field("Place of birth", orDash(e.place_of_birth))}
          ${field("National Insurance no.", orDash(e.ni_number))}
          ${field("Gender", orDash(e.gender_types))}
        </div>`
      ) +
      card(
        "Contact",
        `<div class="grid">
          ${field("Work email", orDash(e.email))}
          ${field("Mobile", orDash([text(e.phone_country_code), text(e.phone)].filter(Boolean).join(" ")))}
          ${field("Work phone", orDash(e.work_phone))}
          ${field("Address", orDash(e.address))}
          ${field("Postcode", orDash(e.postcode))}
        </div>`
      ) +
      card(
        "Emergency contact",
        `<div class="grid">
          ${field("Name", orDash(e.emergency_contact_name))}
          ${field("Relationship", orDash(e.emergency_contact_relationship))}
          ${field(
            "Telephone",
            orDash(
              [text(e.emergency_contact_country_code), text(e.emergency_contact_phone)]
                .filter(Boolean)
                .join(" ")
            )
          )}
        </div>`
      ),
  });

  /* Employment */
  sections.push({
    title: "Employment & status",
    chapter: "Personal details & employment",
    count: null,
    html: card(
      "Position",
      `<div class="grid">
        ${field("Job role", orDash(e.role))}
        ${field("Depot / location", orDash(e.depot))}
        ${field("Assessing manager", orDash(e.manager))}
        ${field("Start date", fmtDate(e.start_date))}
        ${field("Company", orDash(e.company_name || e.company_code))}
        ${field("Work status", orDash(e.status))}
      </div>
      <div class="grid grid-2" style="margin-top:4mm">
        ${field("Additional roles", orDash(e.work_status))}
        ${field("Monitoring status", orDash(e.monitoring_status))}
      </div>`
    ),
  });

  /* Licence */
  sections.push({
    title: "Train driving licence",
    chapter: "Licence & cab passes",
    count: null,
    html: card(
      "Licence details",
      `<div class="grid">
        ${field("Licence number", orDash(e.licence_number))}
        ${field("Issued", fmtDate(e.licence_issue_date))}
        ${field("Expires", fmtDate(e.licence_expiry))}
        ${field("Language", orDash(e.licence_language))}
        ${field("Categories", orDash(e.licence_categories))}
        ${field("Restrictions", orDash(e.licence_restrictions))}
      </div>
      <div class="grid grid-2" style="margin-top:4mm">
        ${field(
          "Details added",
          `${fmtDateTime(e.licence_details_added_at)}${
            text(e.licence_details_added_by) ? ` — ${text(e.licence_details_added_by)}` : ""
          }`
        )}
        ${field(
          "Details confirmed",
          `${fmtDateTime(e.licence_details_confirmed_at)}${
            text(e.licence_details_confirmed_by) ? ` — ${text(e.licence_details_confirmed_by)}` : ""
          }`
        )}
      </div>`
    ),
  });

  /* Cab passes */
  const cabPasses = parseJsonArray(e.cab_passes_json);
  sections.push({
    title: "Cab passes",
    chapter: "Licence & cab passes",
    count: cabPasses.length,
    html: card(
      "Issued passes",
      cabPasses.length
        ? table(
            ["Pass no.", "Type", "Issued", "Expires", "Issued by", "Digital"],
            cabPasses.map((c) => [
              esc(orDash(c.pass_number)),
              esc(orDash(c.pass_type ?? c.type)),
              esc(fmtDate(c.issue_date ?? c.issued_at)),
              expiryChip(c.expiry_date ?? c.expires_at),
              esc(orDash(c.issued_by_name ?? c.issued_by)),
              esc(yesNo(c.digital)),
            ])
          )
        : empty("No cab passes recorded.")
    ),
  });

  /* Trains & routes */
  const competencies = asArray(e.competencies);
  sections.push({
    title: "Trains & routes competence",
    chapter: "Competence & training",
    count: competencies.length,
    html: card(
      "Competence assessments",
      competencies.length
        ? table(
            ["Traction / route", "Assessed", "Assessor", "Result", "Expires", "Notes"],
            competencies
              .slice()
              .sort((a, b) => text(b.date_assessed).localeCompare(text(a.date_assessed)))
              .map((c) => [
                esc(orDash(c.type)),
                esc(fmtDate(c.date_assessed)),
                esc(orDash(c.assessed_by)),
                chipFor(c.result),
                expiryChip(c.expiry_date),
                esc(orDash(c.notes)),
              ])
          )
        : empty("No competence records held.")
    ),
  });

  /* Qualifications & training */
  const qualifications = asArray(e.qualifications);
  const training = asArray(e.training_records);
  sections.push({
    title: "Training & qualifications",
    chapter: "Competence & training",
    count: qualifications.length + training.length,
    html:
      card(
        "Qualifications",
        qualifications.length
          ? table(
              ["Qualification", "Type", "Certificate no.", "Obtained", "Expires"],
              qualifications.map((q) => [
                esc(orDash(q.name)),
                esc(orDash(q.qualification_type)),
                esc(orDash(q.certificate_number)),
                esc(fmtDate(q.date_obtained)),
                expiryChip(q.expiry_date),
              ])
            )
          : empty("No qualifications recorded.")
      ) +
      card(
        "Training records",
        training.length
          ? table(
              ["Course", "Type", "Attended", "Provider", "Status", "Expires"],
              training.map((t) => [
                esc(orDash(t.training_name)),
                esc(orDash(t.type)),
                esc(fmtDate(t.date_attended)),
                esc(orDash(t.provider)),
                chipFor(t.status),
                expiryChip(t.expiry_date),
              ])
            )
          : empty("No training records held.")
      ),
  });

  /* Cycles */
  const cycles = asArray(input.employeeCycles ?? e.employee_cycles);
  const cycleEvents = asArray(input.cycleEvents);
  sections.push({
    title: "Assessment cycles",
    chapter: "Assessment cycles",
    count: cycles.length,
    html:
      card(
        "Assigned cycles",
        cycles.length
          ? table(
              ["Cycle", "Start", "Expiry", "Length", "Status"],
              cycles.map((c) => [
                esc(orDash(c.cycle_title ?? c.title)),
                esc(fmtDate(c.start_date)),
                expiryChip(c.expiry_date),
                esc(text(c.expiry_years) ? `${text(c.expiry_years)} yr` : "—"),
                chipFor(c.end_reason ? "Closed" : "Active"),
              ])
            )
          : empty("No cycles assigned.")
      ) +
      (cycleEvents.length
        ? card(
            "Cycle events & monitoring",
            table(
              ["Event", "Window opens", "Window closes", "Status", "Completed", "By"],
              cycleEvents.map((ev) => [
                esc(orDash(ev.event_name)),
                esc(fmtDate(ev.window_open)),
                esc(fmtDate(ev.window_close)),
                chipFor(ev.status),
                esc(fmtDate(ev.completed_at)),
                esc(orDash(ev.completed_by)),
              ])
            )
          )
        : ""),
  });

  /* Medical */
  const medicals = asArray(e.medicals);
  sections.push({
    title: "Medical fitness",
    chapter: "Medical & medication",
    count: medicals.length,
    html: card(
      "Medical assessments",
      medicals.length
        ? table(
            ["Assessment", "Completed", "Expires", "Outcome", "Restrictions", "Latest"],
            medicals
              .slice()
              .sort((a, b) => text(b.date_completed).localeCompare(text(a.date_completed)))
              .map((m) => [
                esc(orDash(m.type)),
                esc(fmtDate(m.date_completed)),
                expiryChip(m.expiry_date),
                chipFor(m.status),
                esc(orDash(m.restrictions)),
                esc(yesNo(m.is_latest)),
              ])
          )
        : empty("No medical recorded."),
      "Special category health data — handle under stricter safeguards."
    ),
  });

  /* Medication checks */
  const meds = asArray(e.medication_declarations);
  sections.push({
    title: "Medication checks",
    chapter: "Medical & medication",
    count: meds.length,
    html: card(
      "Declarations",
      meds.length
        ? table(
            ["Medication", "Dosage", "Prescribed", "Checked", "Outcome", "OH response", "Status"],
            meds.map((m) => [
              esc(orDash(m.medication_name)),
              esc(orDash(m.dosage)),
              esc(yesNo(m.is_prescribed)),
              esc(fmtDate(m.date_checked)),
              chipFor(m.outcome),
              esc(orDash(m.oh_response)),
              chipFor(m.status),
            ])
          )
        : empty("No medication declarations recorded."),
      "Special category health data — handle under stricter safeguards."
    ),
  });

  /* Incidents */
  const incidents = asArray(e.incidents);
  sections.push({
    title: "Incidents & monitoring",
    chapter: "Incidents & safety",
    count: incidents.length,
    html: card(
      "Incident history",
      incidents.length
        ? table(
            ["Date", "Type", "Severity", "Status", "Description", "Action taken"],
            incidents
              .slice()
              .sort((a, b) => text(b.date).localeCompare(text(a.date)))
              .map((i) => [
                esc(fmtDate(i.date)),
                esc(orDash(i.type)),
                chipFor(i.severity),
                chipFor(i.status),
                esc(orDash(i.description)),
                esc(orDash(i.action_taken)),
              ])
          )
        : empty("No incidents recorded.")
    ),
  });

  /* Safety briefs */
  const briefs = asArray(input.safetyBriefs ?? e.safety_briefs);
  sections.push({
    title: "Safety briefs",
    chapter: "Incidents & safety",
    count: briefs.length,
    html: card(
      "Briefings delivered",
      briefs.length
        ? table(
            ["Date", "Brief", "Delivered by", "Acknowledged"],
            briefs.map((b) => [
              esc(fmtDate(b.date ?? b.brief_date)),
              esc(orDash(b.title ?? b.name ?? b.brief_title)),
              esc(orDash(b.delivered_by ?? b.created_by_name ?? b.created_by)),
              esc(yesNo(b.acknowledged ?? b.signed)),
            ])
          )
        : empty("No safety briefs recorded.")
    ),
  });

  /* Experience records */
  const experience = parseJsonArray(e.experience_records_json);
  sections.push({
    title: "Experience records",
    chapter: "Experience & documents",
    count: experience.length,
    html: card(
      "Recorded experience",
      experience.length
        ? table(
            ["Date", "Category", "Detail", "Recorded by"],
            experience.map((r) => [
              esc(fmtDate(r.date ?? r.recorded_at)),
              esc(orDash(r.category ?? r.type)),
              esc(orDash(r.detail ?? r.description ?? r.notes)),
              esc(orDash(r.recorded_by_name ?? r.recorded_by)),
            ])
          )
        : empty("No experience records held.")
    ),
  });

  /* Documents */
  const documents = asArray(input.documents).filter((d) => !d.is_folder);
  sections.push({
    title: "Documents",
    chapter: "Experience & documents",
    count: documents.length,
    html: card(
      "Document register",
      documents.length
        ? table(
            ["Document", "Location", "Added"],
            documents.map((d) => [
              esc(orDash(d.name)),
              esc(orDash(d.path)),
              esc(fmtDateTime(d.created_at)),
            ])
          )
        : empty("No documents held."),
      "File contents are not reproduced in this brochure — only the register of held documents."
    ),
  });

  /* Notes & activity */
  const conversations = asArray(e.note_conversations);
  const notes = asArray(e.notes);
  sections.push({
    title: "Messaging, notes & activity",
    chapter: "Messaging, notes & activity",
    count: notes.length,
    html:
      card(
        "Note threads",
        conversations.length
          ? table(
              ["Thread", "Status", "Started", "Ended", "Locked"],
              conversations.map((c) => [
                esc(orDash(c.title ?? `Thread ${text(c.id).slice(0, 8)}`)),
                chipFor(c.status),
                esc(fmtDate(c.started_at)),
                esc(fmtDate(c.ended_at)),
                esc(yesNo(c.locked)),
              ])
            )
          : empty("No note threads.")
      ) +
      card(
        "Notes",
        notes.length
          ? table(
              ["When", "Author", "Note"],
              notes.map((n) => [
                esc(fmtDateTime(n.timestamp ?? n.created_at)),
                esc(orDash(n.author_name ?? n.author ?? n.author_email)),
                esc(orDash(n.note ?? n.message ?? n.content ?? n.text)),
              ])
            )
          : empty("No notes recorded.")
      ),
  });

  return sections;
}

/* ------------------------------------------------------------ document build */

export function buildEmployeeBrochureHtml(input: BrochureInput): string {
  const e = input.employee;
  const printFriendly = !!input.printFriendly;
  const dark = !printFriendly && !!input.dark;
  const p = palette(dark);
  const bgImage = input.backgroundImage || BG_ASSET_URL;
  const notice = buildEmployeeBrochureDataProtectionNotice();
  const sections = buildSections(input);
  const chapters = groupIntoChapters(sections);
  const generatedAt = new Date();
  const employeeName = text(e.full_name) || "Employee";
  const companyName = text(e.company_name) || text(e.company_code) || "";
  const reference = `RV-${text(e.id).slice(0, 12).toUpperCase()}-${generatedAt.toISOString().slice(0, 10).replace(/-/g, "")}`;
  // Only inline data URIs: a remote src would not resolve inside the print document.
  const producedSignature = /^data:/i.test(String(input.producedBySignature ?? "").trim())
    ? String(input.producedBySignature).trim()
    : "";

  const locked =
    e.locked === true ||
    e.locked === 1 ||
    ["1", "true", "yes"].includes(String(e.locked ?? "").toLowerCase());

  const totalParts = chapters.length + 1;
  const pageFoot = (n: number) =>
    `<div class="page-foot"><span>${esc(employeeName)} — confidential employee record</span><span>${esc(
      reference
    )}</span><span>Part ${n} of ${totalParts}</span></div>`;

  const interior = (title: string, body: string, partNo: number) => `
    <div class="sheet">
      <div class="sheet-body">
        <div class="page-head">
          <div class="page-head-title">${esc(title)}</div>
          <div class="page-head-meta">${esc(employeeName)} · ${esc(text(e.id))}</div>
        </div>
        ${body}
      </div>
      ${pageFoot(partNo)}
    </div>`;

  /* Cover */
  const cover = `
    <div class="sheet cover">
      <div class="cover-inner">
        <div class="cover-top">
          ${
            text(e.cover_logo_url) || text(e.company_logo_url)
              ? `<img class="cover-logo" src="${esc(text(e.cover_logo_url) || text(e.company_logo_url))}" alt="" />`
              : `<div class="cover-brandmark" style="text-align:left">${esc(companyName)}</div>`
          }
          <div class="cover-brandmark">Rail Intel<br />Employee record</div>
        </div>

        <div class="cover-main">
        <div class="cover-eyebrow">Complete employee profile</div>
        <h1 class="cover-title">${esc(employeeName)}</h1>
        <div class="cover-sub">${esc(orDash(e.role))}${
          text(e.depot) ? ` · ${esc(text(e.depot))}` : ""
        }</div>
        <div class="cover-rule"></div>

        <div style="display:flex;align-items:flex-start;gap:6mm">
          ${
            text(e.image_url)
              ? `<img class="cover-portrait" src="${esc(e.image_url)}" alt="" />`
              : ""
          }
          <div style="flex:1">
            <div class="cover-facts">
              <div><div class="cover-fact-k">Employee ID</div><div class="cover-fact-v">${esc(orDash(e.id))}</div></div>
              <div><div class="cover-fact-k">Company</div><div class="cover-fact-v">${esc(orDash(companyName))}</div></div>
              <div><div class="cover-fact-k">Work status</div><div class="cover-fact-v">${esc(orDash(e.status))}</div></div>
              <div><div class="cover-fact-k">Monitoring</div><div class="cover-fact-v">${esc(orDash(e.monitoring_status))}</div></div>
              <div><div class="cover-fact-k">Licence no.</div><div class="cover-fact-v">${esc(orDash(e.licence_number))}</div></div>
              <div><div class="cover-fact-k">Licence expiry</div><div class="cover-fact-v">${esc(fmtDate(e.licence_expiry))}</div></div>
            </div>
          </div>
        </div>

        <div class="cover-badges">
          <div class="cover-badge">Confidential · Not for onward disclosure</div>
          ${
            locked
              ? `<div class="cover-badge cover-badge--locked">Locked for evidential purposes</div>`
              : ""
          }
        </div>

        <div class="cover-notice">
          <div class="notice">
            <p class="notice-title">${esc(notice.title)}</p>
            <p class="notice-body">${esc(notice.body)}</p>
          </div>
        </div>
        </div>

        <div class="cover-foot">
          <div><strong>Reference</strong><br />${esc(reference)}</div>
          <div><strong>Produced</strong><br />${esc(generatedAt.toLocaleString())}</div>
          <div style="text-align:right"><strong>Produced by</strong><br />${esc(
            text(input.producedBy) || "—"
          )}</div>
        </div>
      </div>
    </div>`;

  /* Summary / contents page */
  const summaryBody = `
    ${
      locked
        ? `<div class="lock-banner"><strong>This profile is locked.</strong> Editing is disabled and the record is preserved for evidential purposes${
            text(e.locked_by_name) || text(e.locked_by_email)
              ? ` — locked by ${esc(text(e.locked_by_name) || text(e.locked_by_email))}`
              : ""
          }${text(e.locked_at) ? ` on ${esc(fmtDateTime(e.locked_at))}` : ""}.${
            text(e.lock_reason) ? ` Reason: ${esc(text(e.lock_reason))}` : ""
          }</div>`
        : ""
    }
    <div class="stat-row">
      <div class="stat"><div class="stat-v">${asArray(e.competencies).length}</div><div class="stat-k">Competences</div></div>
      <div class="stat"><div class="stat-v">${asArray(e.qualifications).length + asArray(e.training_records).length}</div><div class="stat-k">Training</div></div>
      <div class="stat"><div class="stat-v">${asArray(e.medicals).length}</div><div class="stat-k">Medicals</div></div>
      <div class="stat"><div class="stat-v">${asArray(e.incidents).length}</div><div class="stat-k">Incidents</div></div>
    </div>
    ${card(
      "Contents",
      `<ul class="toc">${chapters
        .map(
          (chapter, i) =>
            `<li><span class="toc-no">${String(i + 1).padStart(2, "0")}</span><span><span class="toc-chapter">${esc(
              chapter.title
            )}</span><span class="toc-sections">${chapter.sections
              .map(
                (s) =>
                  `${esc(s.title)}${
                    s.count === null ? "" : ` (${s.count})`
                  }`
              )
              .join(" · ")}</span></span><span class="toc-count">Part ${i + 2}</span></li>`
        )
        .join("")}</ul>
      <div class="declaration-block">
        <div class="card-title">Declaration</div>
        <p class="card-hint" style="margin:0">This brochure was generated directly from the Rail Intel record of the named employee at the
         date and time shown on the cover. It reflects the data held at that moment; the live record may have changed since.</p>
         <div class="signoff">
           <div>
             <div class="signoff-line${producedSignature ? " signoff-line--signed" : ""}">${
               producedSignature
                 ? `<img class="signoff-sig" src="${esc(producedSignature)}" alt="Signature of ${esc(
                     text(input.producedBy) || "the producer"
                   )}" />`
                 : ""
             }</div>
             <div class="signoff-k">Produced by (signature)</div>
             ${producedSignature && text(input.producedBy) ? `<div class="signoff-name">${esc(text(input.producedBy))}</div>` : ""}
           </div>
           <div><div class="signoff-line"></div><div class="signoff-k">Received by (signature &amp; date)</div></div>
         </div>
      </div>`
    )}`;

  const body = [
    cover,
    interior("Summary & contents", summaryBody, 1),
    ...chapters.map((chapter, i) =>
      interior(
        chapter.title,
        chapter.sections
          .map((s) =>
            chapter.sections.length > 1 ? `<h4 class="section-label">${esc(s.title)}</h4>${s.html}` : s.html
          )
          .join(""),
        i + 2
      )
    ),
  ].join("");

  return `<!doctype html>
<html lang="en" data-theme="${dark ? "dark" : "light"}">
<head>
<meta charset="utf-8" />
<title>${esc(employeeName)} — Employee Profile (${esc(reference)})</title>
<style>${stylesheet(p, dark, bgImage, printFriendly)}</style>
</head>
<body>${body}</body>
</html>`;
}

/**
 * Render the brochure and open the print dialog. Images are inlined first so they cannot be missing.
 *
 * iOS Safari prints a blank page from an off-screen or zero-size iframe, so phones open a real tab
 * (created synchronously, before any await, to stay inside the user-gesture) and print that instead.
 */
export async function printEmployeeBrochure(input: BrochureInput): Promise<void> {
  const mobilePrint = isIOSFamily();
  const printWindow = mobilePrint ? window.open("", "_blank") : null;

  const backgroundImage = input.printFriendly
    ? ""
    : input.backgroundImage ?? (await loadImageAsDataUrl(BG_ASSET_URL)) ?? BG_ASSET_URL;

  const brandLogoUrl = input.printFriendly ? "/rail-intel-logo-light.png" : "/rail-intel-logo-dark.png";
  const [producedBySignature, companyLogo, portrait, brandLogo] = await Promise.all([
    resolvePrintableImage(input.producedBySignature),
    resolvePrintableImage(input.employee?.company_logo_url),
    resolvePrintableImage(input.employee?.image_url),
    resolvePrintableImage(brandLogoUrl),
  ]);

  const html = buildEmployeeBrochureHtml({
    ...input,
    backgroundImage,
    producedBySignature,
    employee: {
      ...input.employee,
      ...(companyLogo ? { company_logo_url: companyLogo } : {}),
      ...(portrait ? { image_url: portrait } : {}),
      ...(brandLogo ? { cover_logo_url: brandLogo } : {}),
    },
  });

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  if (printWindow) {
    printWindow.location.href = url;
    await printFromWindow(printWindow, () => {
      printWindow.addEventListener(
        "afterprint",
        () => {
          try {
            printWindow.close();
          } catch {
            /* ignore */
          }
          URL.revokeObjectURL(url);
        },
        { once: true }
      );
      window.setTimeout(() => URL.revokeObjectURL(url), 120000);
    });
    return;
  }

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", mobilePrint ? "false" : "true");
  iframe.style.cssText = mobilePrint
    ? "position:fixed;inset:0;width:100%;height:100%;z-index:2147483646;border:0;background:#0c1428;"
    : "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
  document.body.appendChild(iframe);
  iframe.src = url;
  const frameWindow = iframe.contentWindow;
  if (!frameWindow) {
    iframe.remove();
    URL.revokeObjectURL(url);
    throw new Error("Unable to open a print view.");
  }
  await printFromWindow(frameWindow, () => {
    window.setTimeout(() => {
      iframe.remove();
      URL.revokeObjectURL(url);
    }, 60_000);
  });
}

function isIOSFamily(): boolean {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

function waitForDocumentImages(doc: Document): Promise<void> {
  const images = Array.from(doc.images ?? []);
  if (images.length === 0) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((done) => {
              img.addEventListener("load", () => done(), { once: true });
              img.addEventListener("error", () => done(), { once: true });
            })
    )
  ).then(() => undefined);
}

function printFromWindow(win: Window, afterPrint: () => void): Promise<void> {
  return new Promise((resolve) => {
    let printed = false;
    const trigger = () => {
      if (printed) return;
      printed = true;
      void (async () => {
        try {
          await waitForDocumentImages(win.document);
          win.focus();
          win.print();
        } finally {
          afterPrint();
          resolve();
        }
      })();
    };
    win.addEventListener("load", () => window.setTimeout(trigger, 200), { once: true });
    window.setTimeout(trigger, 4000);
  });
}
